import { Handle, Position } from '@xyflow/react';
import { Stack, Select, NumberInput, Text, Anchor, Box } from '@mantine/core';
import { IconRobot } from '@tabler/icons-react';
import { useEffect, useRef } from 'react';
import { useSubscription } from 'urql';
import { Link } from '@tanstack/react-router';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps, HandleDefinition } from '../types';
import { aiAgentWidgetCore, type AIAgentConfig } from '@legacy-shared/widgets/core/aiAgent';
import { graphql } from '../../lib/graphql';
import { useOrg } from '../../hooks/use-org';

export { type AIAgentConfig } from '@legacy-shared/widgets/core/aiAgent';

const PersonasSubscription = graphql(`
  subscription WidgetPersonas($organizationId: Uuid!) {
    personas(
      where: { organizationId: { _eq: $organizationId }, deletedAt: { _isNull: true } }
      orderBy: { createdAt: ASC }
    ) {
      id
      name
    }
  }
`);

const PersonaOutputHandlesSubscription = graphql(`
  subscription WidgetPersonaOutputHandles($personaId: Uuid!) {
    personasByPk(id: $personaId) {
      id
      outputHandles
    }
  }
`);

type PersonaOutputHandle = { id: string; name: string };

function isPersonaOutputHandle(value: unknown): value is PersonaOutputHandle {
  if (!value || typeof value !== 'object') return false;
  const handle = value as Partial<PersonaOutputHandle>;
  return typeof handle.id === 'string' && typeof handle.name === 'string';
}

function readOutputHandles(value: unknown): PersonaOutputHandle[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isPersonaOutputHandle).map((h) => ({ id: h.id, name: h.name }));
}

const inputTypeOptions = [
  { value: 'speech', label: 'Speech' },
  { value: 'dtmf', label: 'DTMF (Keypad)' },
  { value: 'dtmf speech', label: 'DTMF + Speech' },
];

const HANDLE_SPACING = 20;
const HANDLE_TOP_OFFSET = 15;

function usePersonas() {
  const { organizationId } = useOrg();

  const [{ data }] = useSubscription({
    query: PersonasSubscription,
    variables: { organizationId: organizationId ?? '' },
    pause: !organizationId,
  });

  return data?.personas;
}

function AIAgentNode({ id, data, selected }: WidgetNodeProps<AIAgentConfig>) {
  const personas = usePersonas();

  const persona = personas?.find((p) => p.id === data.config.personaId);
  const subtitle = persona?.name || 'No persona selected';

  const outputs = getDynamicOutputs(data.config);
  const handleCount = outputs.filter((o) => o.position === 'right').length;
  const minHeight = handleCount > 2 ? HANDLE_TOP_OFFSET + handleCount * HANDLE_SPACING : undefined;

  return (
    <WidgetNodeShell
      icon={IconRobot}
      title={data.label || 'AI Agent'}
      subtitle={subtitle}
      configured={!!data.config.personaId}
      errors={data.errors}
      selected={selected}
      color="violet"
      minHeight={minHeight}
    >
      <Handle type="target" position={Position.Top} id="in" />

      {outputs.map((o, index) => (
        <LabeledHandle
          key={o.id}
          type="source"
          position={o.position}
          id={o.id}
          label={o.label}
          offset={o.position === 'right' ? `${HANDLE_TOP_OFFSET + index * HANDLE_SPACING}px` : '50%'}
          nodeId={id}
        />
      ))}
    </WidgetNodeShell>
  );
}

function AIAgentConfigPanel({ config, onChange }: ConfigPanelProps<AIAgentConfig>) {
  const personas = usePersonas();

  const [{ data: personaData }] = useSubscription({
    query: PersonaOutputHandlesSubscription,
    variables: { personaId: config.personaId },
    pause: !config.personaId,
  });

  const fullPersona = personaData?.personasByPk;

  const prevPersonaHandlesRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!fullPersona) return;
    const personaHandles = readOutputHandles(fullPersona.outputHandles);
    const serialized = JSON.stringify(personaHandles);
    if (serialized === prevPersonaHandlesRef.current) return;
    prevPersonaHandlesRef.current = serialized;
    onChange({ ...config, outputHandles: personaHandles.length > 0 ? personaHandles : undefined });
  }, [fullPersona, config, onChange]);

  const personaOptions = (personas || []).map((p) => ({
    value: p.id,
    label: p.name,
  }));

  return (
    <Stack gap="md">
      <Select
        label="Persona"
        description="The AI persona that will handle the conversation"
        placeholder="Select a persona"
        value={config.personaId || null}
        onChange={(value) => onChange({ ...config, personaId: value || '', outputHandles: undefined })}
        data={personaOptions}
        searchable
      />

      {personaOptions.length === 0 && (
        <Box p="sm" bg="var(--mantine-color-yellow-light)" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
          <Text size="sm">
            No personas found.{' '}
            <Anchor component={Link} to="/personas" target="_blank">
              Create a persona
            </Anchor>{' '}
            to use the AI Agent widget.
          </Text>
        </Box>
      )}

      <Select
        label="Input Type"
        description="How the caller provides input to the AI"
        value={config.inputType}
        onChange={(value) => onChange({ ...config, inputType: value || 'speech' })}
        data={inputTypeOptions}
      />

      <NumberInput
        label="Max Turns"
        description="Maximum conversation turns before routing to the Max Turns output"
        value={config.maxTurns}
        onChange={(value) => onChange({ ...config, maxTurns: typeof value === 'number' ? value : 10 })}
        min={1}
        max={50}
      />

      <NumberInput
        label="Timeout (seconds)"
        description="How long to wait for caller input"
        value={config.timeout}
        onChange={(value) => onChange({ ...config, timeout: typeof value === 'number' ? value : 10 })}
        min={1}
        max={30}
      />

      {config.personaId && (
        <Text size="sm" c="dimmed">
          <Anchor component={Link} to={`/personas/${config.personaId}`} target="_blank">
            Edit persona settings
          </Anchor>
        </Text>
      )}
    </Stack>
  );
}

function getDynamicOutputs(config: AIAgentConfig): HandleDefinition[] {
  const outputs: HandleDefinition[] = (config.outputHandles ?? []).map((h) => ({
    id: h.id,
    type: 'ai-exit' as const,
    label: h.name,
    position: 'right' as const,
  }));

  outputs.push({
    id: 'maxTurns',
    type: 'timeout' as const,
    label: 'Max Turns',
    position: 'bottom' as const,
  });

  return outputs;
}

export const aiAgentWidget: WidgetDefinition<AIAgentConfig> = {
  ...aiAgentWidgetCore,
  icon: 'IconRobot',
  variables: aiAgentWidgetCore.variables,

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [],
  },

  getDynamicOutputs,

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: AIAgentNode,
    configPanel: AIAgentConfigPanel,
    preview: (config) => (config.personaId ? 'AI Conversation' : 'No persona'),
  },

  validation: {
    ...aiAgentWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
